/**
 * FarmIrrigationDetail — one farm's irrigation, in the shape of the landing
 * page rather than the device list.
 *
 * Opening a farm used to land on Devices: controllers, node cards, relay
 * bitmasks. That is the right screen for whoever installed the hardware and the
 * wrong one for everybody else, who came to see whether the farm is watering
 * and what is set to run. So this page answers the same three questions the
 * overview answers — what is running, what is next, what is stopping water —
 * scoped to one farm, and treats the map and the schedules as doors:
 *
 *   map      → the controller's Live Map tab, where zones are drawn and placed
 *   schedule → the controller's Schedules tab, focused on that program
 *   devices  → still one click away in the header, for whoever wants it
 *
 * Four tabs (?tab=): Live; Schedules — the builder and rain skip, per site;
 * Soil — every soil probe on the farm worst first,
 * because a dry block is the sensor reading that costs a crop; and Setup —
 * the farm's sites, nodes and sensors, each one tap from its own page, in
 * place of the Devices page that used to stand between a farm and its nodes.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Calendar,
  ChevronRight,
  Droplets,
  Map as MapIcon,
  Maximize2,
  RefreshCw,
  Settings,
  Sprout,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import ControllerStopControl from '../../components/irrigation/ControllerStopControl';
import FarmSetupPanel from '../../components/irrigation/FarmSetupPanel';
import FarmSchedulesTab from '../../components/irrigation/FarmSchedulesTab';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import FarmOverviewMap from '../../components/irrigation/FarmOverviewMap';
import FarmSchedulePanel from '../../components/irrigation/FarmSchedulePanel';
import IrrigationStatusStrip from '../../components/irrigation/IrrigationStatusStrip';
import RunningZoneCard from '../../components/irrigation/RunningZoneCard';
import SoilMoisturePanel, { SoilSummaryStrip } from '../../components/irrigation/SoilMoisturePanel';
import { farmSoilChannels } from '../../utils/soilMoisture';
import UpcomingRunsList from '../../components/irrigation/UpcomingRunsList';
import useIrrigationLive from '../../hooks/useIrrigationLive';
import espnowService, { type ScheduleProgram } from '../../services/espnowIrrigation';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone } from '../../utils/timezone';
import '../../styles/GlobalPageStyles.css';
import '../../styles/IrrigationLive.css';

const UPCOMING_SHOWN = 6;

type FarmTab = 'live' | 'schedules' | 'soil' | 'setup';

export default function FarmIrrigationDetail() {
  const { farmId: farmIdParam } = useParams<{ farmId: string }>();
  const farmId = Number(farmIdParam);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: FarmTab =
    tabParam === 'schedules' || tabParam === 'soil' || tabParam === 'setup' ? tabParam : 'live';
  const setTab = (next: FarmTab) => {
    const p = new URLSearchParams(searchParams);
    if (next === 'live') p.delete('tab');
    else p.set('tab', next);
    // A site and program belong to the link that opened Schedules.
    if (next !== 'schedules') {
      p.delete('site');
      p.delete('program');
    }
    setSearchParams(p, { replace: true });
  };
  const orgTz = useOrgTimezone();
  const { data, live, loading, refreshing, error, lastUpdated, refresh } = useIrrigationLive();
  const { hasAccess: isIrrigationAdmin } = useModuleLicense('irrigation', 'admin');

  const [programs, setPrograms] = useState<Record<string, ScheduleProgram[]>>({});
  const [programsLoading, setProgramsLoading] = useState(true);

  useAppRefresh(refresh);

  const farm = useMemo(
    () => data?.farms.find((f) => f.id === farmId) ?? null,
    [data, farmId]
  );

  const controllers = useMemo(() => farm?.controllers ?? [], [farm]);

  /**
   * Programs are fetched per controller, keyed off the controller ids alone —
   * the farm object itself is replaced on every 20 s poll, and depending on it
   * would refetch every program on the farm three times a minute for data that
   * only changes when somebody edits it.
   */
  const controllerKey = useMemo(
    () => controllers.map((c) => c.controller_id).sort().join(','),
    [controllers]
  );

  // Read on Live, where they are listed, and again on every return to it:
  // the Schedules tab may have changed them.
  const onLive = tab === 'live';
  useEffect(() => {
    if (!onLive) return;
    const ids = controllerKey ? controllerKey.split(',') : [];
    if (ids.length === 0) {
      setPrograms({});
      setProgramsLoading(false);
      return;
    }
    let cancelled = false;
    setProgramsLoading(true);
    // Best-effort per controller: one site that will not answer must not blank
    // out the programs we can read from the others.
    void Promise.all(
      ids.map(async (id) => {
        try {
          return [id, await espnowService.getPrograms(id)] as const;
        } catch {
          return [id, [] as ScheduleProgram[]] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setPrograms(Object.fromEntries(entries));
      setProgramsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [controllerKey, onLive]);

  const runs = useMemo(() => live.runs.filter((r) => r.farmId === farmId), [live.runs, farmId]);
  const upcoming = useMemo(
    () => live.upcoming.filter((u) => u.farmId === farmId).slice(0, UPCOMING_SHOWN),
    [live.upcoming, farmId]
  );
  const alerts = useMemo(
    () => live.alerts.filter((a) => a.farmId === farmId),
    [live.alerts, farmId]
  );
  const soilDry = useMemo(
    () => (farm ? farmSoilChannels(farm).filter((r) => r.channel.status === 'dry').length : 0),
    [farm]
  );

  /** Node health for this farm only — the strip's global counts would mislead. */
  const nodes = useMemo(() => {
    let online = 0;
    let total = 0;
    for (const c of controllers) {
      for (const r of c.routers) {
        total++;
        if (r.online) online++;
      }
    }
    return { online, total };
  }, [controllers]);

  /**
   * Which controller the map opens. A farm usually has one; where it has
   * several, the one with placed nodes is the one whose live map is worth
   * looking at, because an unplaced controller's map is empty.
   */
  const mapControllerId = useMemo(() => {
    const placed = controllers.find((c) => c.routers.some((r) => r.lat != null && r.lng != null));
    return (placed ?? controllers[0])?.controller_id ?? null;
  }, [controllers]);

  const openLiveMap = () => {
    if (mapControllerId) navigate(`/irrigation/espnow/${mapControllerId}?tab=map`);
  };

  const secondsSinceUpdate = lastUpdated
    ? Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000))
    : null;

  if (loading) {
    return (
      <>
        <PageHeader title="Farm irrigation" icon={<Droplets className="h-6 w-6 text-blue-600" />} />
        <div className="mx-auto max-w-7xl px-4 py-6">
          <div className="flex h-64 items-center justify-center text-gray-400">
            <RefreshCw className="mr-2 h-6 w-6 animate-spin" />
            Loading live irrigation status…
          </div>
        </div>
      </>
    );
  }

  if (error || !farm) {
    return (
      <>
        <PageHeader title="Farm irrigation" icon={<Droplets className="h-6 w-6 text-blue-600" />} />
        <div className="mx-auto max-w-7xl px-4 py-6">
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error ?? 'That farm is not in your irrigation overview.'}
            <Link to="/irrigation" className="ml-3 font-semibold underline">
              Back to irrigation
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={farm.name}
        subtitle="Live irrigation"
        icon={<Droplets className="h-6 w-6 text-blue-600" />}
        actionButtons={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refresh()}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 sm:mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        }
      />
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Above the status strip, because the strip's headline for a held farm
            is "no zones watering" — true, and the single most misleading thing
            this page can say on its own. One control per site: the farm is the
            unit somebody thinks in, the controller is the unit a command is
            addressed to, and on a multi-site farm the difference matters. */}
        {controllers.length > 0 && (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
            {controllers.map((c) => (
              <ControllerStopControl
                key={c.controller_id}
                controller={c}
                showName={controllers.length > 1}
                onChanged={() => void refresh()}
              />
            ))}
          </div>
        )}

        <div className="mb-4 grid w-full grid-cols-4 rounded-lg border border-gray-200 bg-gray-50 p-0.5 sm:inline-flex sm:w-auto" role="tablist" aria-label="Farm irrigation">
          {([
            { key: 'live', label: 'Live', icon: <Droplets className="h-4 w-4" /> },
            { key: 'schedules', label: 'Schedules', icon: <Calendar className="h-4 w-4" /> },
            { key: 'soil', label: 'Soil', icon: <Sprout className="h-4 w-4" /> },
            { key: 'setup', label: 'Setup', icon: <Settings className="h-4 w-4" /> },
          ] as const).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`flex min-w-0 flex-col items-center gap-0.5 rounded-md px-1 py-1.5 text-xs font-medium sm:flex-row sm:gap-1.5 sm:px-4 sm:text-sm ${
                tab === t.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.icon}
              {t.label}
              {t.key === 'soil' && soilDry > 0 && (
                <span className="rounded-full bg-red-600 px-1.5 text-xs font-semibold text-white">{soilDry}</span>
              )}
            </button>
          ))}
        </div>

        {tab === 'schedules' ? (
          <FarmSchedulesTab controllers={controllers} orgTimezone={orgTz} />
        ) : tab === 'setup' ? (
          <FarmSetupPanel farm={farm} timezone={orgTz} isAdmin={isIrrigationAdmin} />
        ) : tab === 'soil' ? (
          <div>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <Sprout className="h-4 w-4 text-green-600" />
              Soil moisture, driest first
            </h2>
            <SoilMoisturePanel farm={farm} timezone={orgTz} />
          </div>
        ) : (
        <>
        <IrrigationStatusStrip
          scopeFarms={[farm]}
          runs={runs}
          upcoming={upcoming}
          routersOnline={nodes.online}
          routersTotal={nodes.total}
          hasSchedule={live.hasBridgeSchedule || live.hasScheduleData}
          secondsSinceUpdate={secondsSinceUpdate}
        />

        <div className="mt-4">
          <SoilSummaryStrip farm={farm} href={`/irrigation/farms/${farm.id}?tab=soil`} />
        </div>

        {/* ── Map + what's running ─────────────────────────────────────────── */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                <MapIcon className="h-4 w-4 text-gray-400" />
                Farm map
              </h2>
              {mapControllerId && (
                <button
                  type="button"
                  onClick={openLiveMap}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                  Open live map
                </button>
              )}
            </div>
            <FarmOverviewMap
              boundaryGeometry={farm.boundary_geometry}
              centerPoint={farm.center_point}
              controllers={farm.controllers}
              sensors={farm.sensors}
              weatherStations={farm.weather_stations}
              height={420}
              onClick={mapControllerId ? openLiveMap : undefined}
            />
            {mapControllerId && (
              <p className="mt-1.5 text-xs text-gray-400">
                Tap the map for the live view — zones, nodes and today's timeline.
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                <Droplets className="h-4 w-4 text-blue-500" />
                Running now
              </h2>
              {runs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
                  No valves are open.
                </div>
              ) : (
                <div className="space-y-3">
                  {runs.map((run) => (
                    <RunningZoneCard key={run.key} run={run} showFarm={false} />
                  ))}
                </div>
              )}
            </div>

            <UpcomingRunsList upcoming={upcoming} />
          </div>
        </div>

        {/* ── Schedules ────────────────────────────────────────────────────── */}
        <div className="mt-8">
          <FarmSchedulePanel
            farmId={farm.id}
            controllers={controllers}
            programsByController={programs}
            loading={programsLoading}
            orgTimezone={orgTz}
          />
        </div>

        {/* ── Anything stopping water arriving ─────────────────────────────── */}
        {alerts.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Needs attention ({alerts.length})
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {alerts.map((alert) => (
                <Link
                  key={alert.key}
                  to={alert.href}
                  className={`flex items-start gap-3 rounded-xl border p-3 transition hover:shadow-sm ${
                  alert.severity === 'error'
                    ? 'border-red-200 bg-red-50 hover:border-red-300'
                    : 'border-amber-200 bg-amber-50 hover:border-amber-300'
                }`}
                >
                  <AlertTriangle
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                    alert.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                  }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold ${
                      alert.severity === 'error' ? 'text-red-800' : 'text-amber-800'
                    }`}
                    >
                      {alert.title}
                    </p>
                    <p className="text-xs text-gray-600">{alert.detail}</p>
                  </div>
                  <ChevronRight className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── Sensors on this farm ─────────────────────────────────────────── */}
        {farm.sensors.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
              <Activity className="h-4 w-4 text-violet-500" />
              Sensors ({farm.sensors.length})
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {farm.sensors.map((sensor) => (
                <Link key={sensor.device_id} to={`/irrigation/sensors/${sensor.device_id}`}>
                  <Card className="h-full transition hover:shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${
                          sensor.online ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        />
                        <p className="truncate text-sm font-medium text-gray-900">
                          {sensor.name || sensor.device_id}
                        </p>
                      </div>
                      <p className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">
                        {sensor.latest_value != null
                          ? `${sensor.latest_value}${sensor.type_unit ?? ''}`
                          : '—'}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {sensor.type_display_name || sensor.type_key}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        </>
        )}

      </div>
    </>
  );
}
